import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { exchangeNotionCodeForTokens } from '../../services/notionService';

type CallbackStatus = 'loading' | 'success' | 'error';

export default function NotionOAuthCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<CallbackStatus>('loading');
    const [message, setMessage] = useState('Processing Notion authentication...');
    const [workspaceName, setWorkspaceName] = useState('');
    const processedRef = useRef(false);

    useEffect(() => {
        const handleCallback = async () => {
            if (processedRef.current) return;
            processedRef.current = true;

            const code = searchParams.get('code');
            const state = searchParams.get('state');
            const error = searchParams.get('error');

            // Check for OAuth error
            if (error) {
                setStatus('error');
                setMessage(error === 'access_denied'
                    ? 'You cancelled the Notion authorization.'
                    : `Authorization failed: ${error}`
                );
                return;
            }

            // Verify code exists
            if (!code) {
                setStatus('error');
                setMessage('No authorization code received from Notion.');
                return;
            }

            // Verify state for CSRF protection
            const storedState = localStorage.getItem('notion_oauth_state');
            if (!state || state !== storedState) {
                console.error('State mismatch', { state, storedState });
                setStatus('error');
                setMessage('Invalid state parameter. Please try again.');
                return;
            }

            // Exchange code for tokens
            setMessage('Connecting to Notion workspace...');
            const tokens = await exchangeNotionCodeForTokens(code);

            if (!tokens) {
                setStatus('error');
                setMessage('Failed to connect to Notion. Please try again.');
                return;
            }

            // Clear stored state
            localStorage.removeItem('notion_oauth_state');

            // Success!
            setWorkspaceName(tokens.workspace_name);
            setStatus('success');
            setMessage(`Successfully connected to ${tokens.workspace_name}!`);

            // Redirect after a delay
            setTimeout(() => {
                navigate('/app/connections', { replace: true });
            }, 2000);
        };

        handleCallback();
    }, [searchParams, navigate]);

    return (
        <div className="min-h-screen bg-[#F5F8FA] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md text-center shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                {status === 'loading' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-[#191919]/10 flex items-center justify-center mx-auto mb-4">
                            <Loader2 className="w-8 h-8 text-[#191919] animate-spin" />
                        </div>
                        <h1 className="text-xl font-semibold text-[#1A2832] mb-2">
                            Connecting to Notion...
                        </h1>
                        <p className="text-sm text-[#7B8C9B]">{message}</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-[#D5F2EA] flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-8 h-8 text-[#3DA58A]" />
                        </div>
                        <h1 className="text-xl font-semibold text-[#1A2832] mb-2">
                            Notion Connected!
                        </h1>
                        <p className="text-sm text-[#7B8C9B] mb-2">{message}</p>
                        {workspaceName && (
                            <p className="text-xs text-[#0D5F6B] font-medium mb-4">
                                Workspace: {workspaceName}
                            </p>
                        )}
                        <p className="text-xs text-[#7B8C9B]">Redirecting to connections...</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-[#FFEAE7] flex items-center justify-center mx-auto mb-4">
                            <XCircle className="w-8 h-8 text-[#D66B5C]" />
                        </div>
                        <h1 className="text-xl font-semibold text-[#1A2832] mb-2">
                            Connection Failed
                        </h1>
                        <p className="text-sm text-[#7B8C9B] mb-6">{message}</p>
                        <button
                            onClick={() => navigate('/app/connections', { replace: true })}
                            className="px-6 py-3 rounded-xl bg-[#0D5F6B] text-white font-semibold"
                        >
                            Back to Connections
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
